import { ChevronRight } from 'lucide-react';
import * as React from 'react';

import type { CampaignDetailDto, SeniorityLevelDto } from '@/api/campaign-client';
import { useAddProfileScopeBatch, useCatalogTree } from '@/api/use-campaigns';
import { Button } from '@/components/ui/button';
import { Panel } from '@/components/ui/panel';
import { catalogVersionLabel } from '@/lib/catalog-version-label';
import { cn } from '@/lib/utils';
import {
  DARK_BUTTON,
  plural,
  TEXT_BUTTON,
  TitleMono,
} from '@/routes/admin/campaigns/detail-ui';
import {
  campaignLevelsOf,
  cellsToAdd,
  filterCatalog,
  flattenTree,
  isFullyScoped,
  nodeState,
  scopedLevelsByProfile,
  type FilteredSubFamily,
} from '@/routes/admin/campaigns/profile-scope-model';

// =============================================================================
// catalog-panel.tsx — Panneau « Catalogue » du détail campagne (sélection de profils à ajouter).
// =============================================================================
// Extrait de campaign-detail-screen.tsx (ProfileTreeSelector), comportement conservé : V-CREATE (DBU §2.5
// l.1131) sélection par familles / sous-familles / mots-clés ; la sélection d'un profil inclut AUTOMATIQUEMENT
// tous ses niveaux (availableLevels) ; ajout EN LOT idempotent ; R25 sélection PAR BLOC tri-état dérivée de
// `selected`, cascade sur les profils VISIBLES ; le filtre est un PROJECTEUR (ne désélectionne jamais).
//
// Nouveautés validées GATE_HUMAN 2026-09-16 : compteur de la recherche, sous-famille « aucun match » grisée,
// mention « au périmètre » avec case désactivée, comptes « sélectionnés » et « cellules ».

/** Case native tri-état : `indeterminate` est une propriété DOM, posée via `ref`/`useEffect`. */
export function TriStateCheckbox({
  checked,
  indeterminate,
  onChange,
  ariaLabel,
  disabled = false,
}: {
  readonly checked: boolean;
  readonly indeterminate: boolean;
  readonly onChange: () => void;
  readonly ariaLabel: string;
  readonly disabled?: boolean;
}): React.JSX.Element {
  const ref = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (ref.current !== null) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="size-4 shrink-0"
      aria-label={ariaLabel}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
  );
}

function toggleInSet<T>(prev: ReadonlySet<T>, value: T): ReadonlySet<T> {
  const next = new Set(prev);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
}

export function CatalogPanel({
  campaign,
}: {
  readonly campaign: CampaignDetailDto;
}): React.JSX.Element {
  // P-8 : le NUMÉRO de version quand l'API le résout, l'uuid tronqué sinon. `TitleMono` (chasse fixe)
  // n'est conservé QUE pour le repli : il dit « identifiant technique », ce qu'un numéro de version n'est
  // pas. L'uuid complet reste en infobulle dans les deux cas.
  const catalogVersion = catalogVersionLabel(
    campaign.catalogVersionId,
    campaign.catalogVersionNumber,
  );
  return (
    <Panel
      headingId="detail-catalog-heading"
      title={
        <>
          Catalogue{' '}
          <span title={campaign.catalogVersionId}>
            {catalogVersion.isFallback ? (
              <TitleMono>figé {catalogVersion.text}</TitleMono>
            ) : (
              <span className="font-medium">figé {catalogVersion.text}</span>
            )}
          </span>
        </>
      }
      aside={
        <span className="text-xs">
          Ajouter un profil inclut tous ses niveaux : Débutant, Maîtrisant, Expert, Référent
        </span>
      }
    >
      <ProfileTreeSelector campaign={campaign} />
    </Panel>
  );
}

function ProfileTreeSelector({
  campaign,
}: {
  readonly campaign: CampaignDetailDto;
}): React.JSX.Element {
  const tree = useCatalogTree();
  const addBatch = useAddProfileScopeBatch();
  const [keyword, setKeyword] = React.useState('');
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(new Set());
  // Repli manuel des familles / sous-familles (clé) ; une sous-famille sans match est repliée d'office.
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(new Set());

  const profiles = React.useMemo(
    () => (tree.data === undefined ? [] : flattenTree(tree.data)),
    [tree.data],
  );
  const scoped = React.useMemo(
    () => scopedLevelsByProfile(campaign.profileScopes),
    [campaign.profileScopes],
  );
  const filtered = React.useMemo(
    () => filterCatalog(tree.data ?? [], keyword),
    [tree.data, keyword],
  );

  const isDisabled = React.useCallback(
    (profileCode: string, levels: readonly SeniorityLevelDto[]): boolean =>
      isFullyScoped(levels, scoped.get(profileCode)),
    [scoped],
  );

  /** Codes SÉLECTIONNABLES visibles sous une sous-famille (les profils déjà au périmètre sont exclus). */
  const selectableCodes = React.useCallback(
    (sf: FilteredSubFamily): string[] =>
      sf.profils
        .filter((p) => !isDisabled(p.profileCode, campaignLevelsOf(p)))
        .map((p) => p.profileCode),
    [isDisabled],
  );

  // Cocher un bloc (famille / sous-famille) alimente `selected` EN CASCADE sur ses descendants VISIBLES ;
  // l'unité qui atteint l'API reste {profileCode, seniorityLevel}.
  const toggleNode = React.useCallback((codes: readonly string[]): void => {
    setSelected((prev) => {
      const state = nodeState(codes, prev);
      const next = new Set(prev);
      for (const c of codes) {
        if (state === 'all') {
          next.delete(c);
        } else {
          next.add(c);
        }
      }
      return next;
    });
  }, []);

  const cells = cellsToAdd(profiles, selected, scoped);
  const selectedCodes = profiles
    .filter((p) => selected.has(p.profileCode))
    .map((p) => p.profileCode);

  const onAddSelection = React.useCallback((): void => {
    // Inclusion AUTO des niveaux : chaque profil sélectionné est déplié en N items × availableLevels.
    const items = profiles
      .filter((p) => selected.has(p.profileCode))
      .flatMap((p) =>
        p.availableLevels.map((seniorityLevel) => ({
          profileCode: p.profileCode,
          seniorityLevel,
        })),
      );
    if (items.length === 0) {
      return;
    }
    addBatch.mutate(
      { campaignId: campaign.campaignId, body: { items } },
      { onSuccess: () => setSelected(new Set()) },
    );
  }, [addBatch, campaign.campaignId, profiles, selected]);

  if (tree.isPending) {
    return (
      <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
        Chargement du catalogue…
      </p>
    );
  }
  if (tree.isError) {
    return (
      <p role="alert" className="px-6 py-4 text-sm text-destructive">
        Erreur de chargement du catalogue : {tree.error.message}
      </p>
    );
  }
  if (profiles.length === 0) {
    return (
      <p role="status" className="px-6 py-4 text-sm text-muted-foreground">
        Aucun catalogue publié : la sélection par familles n&apos;est pas disponible.
      </p>
    );
  }

  const kw = keyword.trim();

  return (
    <div>
      <div className="px-6 pt-4">
        <label htmlFor="profile-keyword" className="sr-only">
          Filtrer par famille, sous-famille ou mots-clés
        </label>
        <div className="flex h-10 items-center gap-3 rounded-md border border-input bg-background px-3 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
          <input
            id="profile-keyword"
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Rechercher un profil, une famille, un code…"
            className="min-w-0 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none"
          />
          {kw.length > 0 ? (
            <>
              <span
                role="status"
                className="shrink-0 font-mono text-xs text-muted-foreground"
              >
                {plural(filtered.matchedProfiles, 'profil')} ·{' '}
                {plural(filtered.matchedSubFamilies, 'sous-famille')}
              </span>
              <button
                type="button"
                className={TEXT_BUTTON}
                onClick={() => setKeyword('')}
                aria-label="Effacer la recherche"
              >
                Effacer
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="mt-3 max-h-96 overflow-y-auto border-t border-border">
        {filtered.families.length === 0 ? (
          <p role="status" className="px-6 py-3 text-sm text-muted-foreground">
            Aucun profil ne correspond au filtre.
          </p>
        ) : (
          <ul aria-label="Profils du catalogue à inclure dans le périmètre">
            {filtered.families.map((fam) => {
              const famKey = `f:${fam.famille}`;
              const famOpen = !collapsed.has(famKey);
              const famCodes = fam.sousFamilles.flatMap((sf) => selectableCodes(sf));
              const famState = nodeState(famCodes, selected);
              return (
                <li key={fam.famille} className="border-b border-border last:border-b-0">
                  <div className="flex items-center gap-3 bg-muted/40 px-6 py-2.5">
                    <button
                      type="button"
                      aria-expanded={famOpen}
                      aria-label={`${famOpen ? 'Replier' : 'Déplier'} la famille ${fam.famille}`}
                      onClick={() => setCollapsed((p) => toggleInSet(p, famKey))}
                      className="rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <ChevronRight
                        aria-hidden="true"
                        className={cn('size-4 transition-transform', famOpen ? 'rotate-90' : undefined)}
                      />
                    </button>
                    <TriStateCheckbox
                      checked={famState === 'all'}
                      indeterminate={famState === 'partial'}
                      disabled={famCodes.length === 0}
                      onChange={() => toggleNode(famCodes)}
                      ariaLabel={`Sélectionner la famille ${fam.famille}`}
                    />
                    <span className="min-w-0 flex-1 text-sm font-semibold">{fam.famille}</span>
                    <span className="shrink-0 font-mono text-xs text-muted-foreground">
                      {fam.matchedSubFamilies} / {fam.sousFamilles.length} sous-familles
                    </span>
                  </div>

                  {famOpen ? (
                    <ul aria-label={`Sous-familles de ${fam.famille}`}>
                      {fam.sousFamilles.map((sf) => (
                        <SubFamilyItem
                          key={sf.sousFamille}
                          famille={fam.famille}
                          sf={sf}
                          keyword={kw}
                          collapsed={collapsed.has(`s:${fam.famille}::${sf.sousFamille}`)}
                          onToggleCollapse={() =>
                            setCollapsed((p) =>
                              toggleInSet(p, `s:${fam.famille}::${sf.sousFamille}`),
                            )
                          }
                          selected={selected}
                          selectableCodes={selectableCodes(sf)}
                          scoped={scoped}
                          isDisabled={isDisabled}
                          onToggleNode={toggleNode}
                          onToggleProfile={(code) =>
                            setSelected((p) => toggleInSet(p, code))
                          }
                        />
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-6 py-3">
        <p className="min-w-0 text-sm text-muted-foreground">
          {plural(selectedCodes.length, 'profil sélectionné', 'profils sélectionnés')}
          {selectedCodes.length > 0 ? (
            <span className="font-mono text-xs"> · {selectedCodes.join(', ')}</span>
          ) : null}
        </p>
        <div className="flex items-center gap-2">
          {selected.size > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set())}
              disabled={addBatch.isPending}
            >
              Effacer la sélection
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={DARK_BUTTON}
            onClick={onAddSelection}
            disabled={selected.size === 0 || addBatch.isPending}
          >
            {addBatch.isPending
              ? 'Ajout…'
              : `Ajouter la sélection (${String(selected.size)}) — ${plural(cells, 'cellule')}`}
          </Button>
        </div>
      </div>

      {addBatch.isError ? (
        <p role="alert" className="px-6 pb-3 text-sm text-destructive">
          {addBatch.error.message}
        </p>
      ) : null}
    </div>
  );
}

function SubFamilyItem({
  famille,
  sf,
  keyword,
  collapsed,
  onToggleCollapse,
  selected,
  selectableCodes,
  scoped,
  isDisabled,
  onToggleNode,
  onToggleProfile,
}: {
  readonly famille: string;
  readonly sf: FilteredSubFamily;
  readonly keyword: string;
  readonly collapsed: boolean;
  readonly onToggleCollapse: () => void;
  readonly selected: ReadonlySet<string>;
  readonly selectableCodes: readonly string[];
  readonly scoped: ReturnType<typeof scopedLevelsByProfile>;
  readonly isDisabled: (profileCode: string, levels: readonly SeniorityLevelDto[]) => boolean;
  readonly onToggleNode: (codes: readonly string[]) => void;
  readonly onToggleProfile: (profileCode: string) => void;
}): React.JSX.Element {
  const noMatch = sf.profils.length === 0;
  const open = !noMatch && !collapsed;
  const inScope = sf.profils.filter((p) => scoped.has(p.profileCode)).length;
  const selectedCount = sf.profils.filter((p) => selected.has(p.profileCode)).length;
  const state = nodeState(selectableCodes, selected);

  if (noMatch) {
    return (
      <li className="flex items-center gap-3 py-2 pl-12 pr-6 text-muted-foreground">
        <span className="min-w-0 flex-1 text-sm">{sf.sousFamille}</span>
        <span className="shrink-0 font-mono text-xs">
          aucun match « {keyword} »
        </span>
      </li>
    );
  }

  return (
    <li>
      <div className="flex items-center gap-3 border-t border-border py-2.5 pl-12 pr-6">
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${open ? 'Replier' : 'Déplier'} la sous-famille ${sf.sousFamille}`}
          onClick={onToggleCollapse}
          className="rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronRight
            aria-hidden="true"
            className={cn('size-4 transition-transform', open ? 'rotate-90' : undefined)}
          />
        </button>
        <TriStateCheckbox
          checked={state === 'all'}
          indeterminate={state === 'partial'}
          disabled={selectableCodes.length === 0}
          onChange={() => onToggleNode(selectableCodes)}
          ariaLabel={`Sélectionner la sous-famille ${sf.sousFamille}`}
        />
        <span className="min-w-0 flex-1 text-sm font-medium">{sf.sousFamille}</span>
        <span className="shrink-0 font-mono text-xs text-muted-foreground">
          {inScope > 0 ? `${String(inScope)} au périmètre · ` : ''}
          {selectedCount > 0 ? `${plural(selectedCount, 'sélectionné')} · ` : ''}
          {sf.total}
        </span>
      </div>

      {open ? (
        <ul aria-label={`Profils de ${famille} › ${sf.sousFamille}`}>
          {sf.profils.map((p) => {
            const levels = campaignLevelsOf(p);
            const disabled = isDisabled(p.profileCode, levels);
            const scopedLevels = scoped.get(p.profileCode);
            const partial = !disabled && scopedLevels !== undefined;
            const checked = selected.has(p.profileCode);
            return (
              <li
                key={p.profileCode}
                className={cn(
                  'border-t border-border py-2.5 pl-20 pr-6',
                  checked ? 'bg-dash-band' : undefined,
                )}
              >
                <label
                  className={cn(
                    'flex items-center gap-3 text-sm',
                    disabled ? 'text-muted-foreground' : undefined,
                  )}
                >
                  <input
                    type="checkbox"
                    className="size-4 shrink-0"
                    aria-label={`Inclure le profil ${p.libelle} (${p.profileCode})`}
                    checked={checked}
                    disabled={disabled}
                    onChange={() => onToggleProfile(p.profileCode)}
                  />
                  <span className="min-w-0 flex-1">
                    {p.libelle}{' '}
                    <span className="font-mono text-xs text-muted-foreground">
                      {p.profileCode}
                    </span>
                  </span>
                  {disabled ? (
                    <span className="shrink-0 text-xs text-muted-foreground">au périmètre</span>
                  ) : partial ? (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {scopedLevels.size} / {levels.length} niveaux au périmètre
                    </span>
                  ) : null}
                </label>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
}
